import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";

const ROW_WIDTHS = ["w-40", "w-28", "w-48", "w-32", "w-36"];

interface TableSkeletonProps {
  columns?: number;
}

// Mirrors the flat list: a caption row, then rows with a leading column that
// takes the remaining width and narrow trailing columns.
export function TableSkeleton({ columns = 3 }: TableSkeletonProps) {
  const trailing = Array.from({ length: Math.max(0, columns - 1) }, (_, i) => i);

  return (
    <div role="status" aria-label="Loading">
      <div className="flex h-11 items-center gap-4 border-b px-4">
        <div className="flex-1">
          <Skeleton className="h-3 w-16" />
        </div>
        {trailing.map((i) => (
          <div key={i} className="w-28 shrink-0 md:w-44">
            <Skeleton className="h-3 w-16" />
          </div>
        ))}
      </div>
      {ROW_WIDTHS.map((width, row) => (
        <div key={row} className="flex h-13 items-center gap-4 border-b px-4">
          <div className="flex-1">
            <Skeleton className={`h-3.5 ${width}`} />
          </div>
          {trailing.map((i) => (
            <div key={i} className="w-28 shrink-0 md:w-44">
              <Skeleton className="h-3.5 w-20" />
            </div>
          ))}
        </div>
      ))}
    </div>
  );
}

interface DetailSkeletonProps {
  withTitle?: boolean;
}

export function DetailSkeleton({ withTitle = true }: DetailSkeletonProps) {
  return (
    <div className="mx-auto flex w-full max-w-3xl flex-col gap-6" role="status" aria-label="Loading">
      {withTitle && <Skeleton className="h-8 w-56" />}
      <Card>
        <CardHeader>
          <Skeleton className="h-4 w-32" />
        </CardHeader>
        <CardContent className="flex flex-col gap-6">
          {ROW_WIDTHS.slice(0, 3).map((width) => (
            <div key={width} className="flex flex-col gap-2.5">
              <Skeleton className="h-3 w-20" />
              <Skeleton className={`h-3.5 ${width}`} />
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
}
